/**
 * Аналитика на страница поска
 * Все эвенты описаны в документе
 * @see https://docs.google.com/spreadsheets/d/1ZJftJ2drwj6rNFlf2tjmUkHM7QpfX4T6e0kQwTAiMng/edit#gid=0
 */

import { select, takeEvery, all, takeLatest, call } from 'redux-saga/effects';
import { partialRight } from 'lodash';
import { getLocalizationValues } from '@lt/localization/dist/utils/getValuesFromBackend';
import { getIsMobile, searchTypeSelector } from 'src/store/view/selectors';
import { getSearchParamsToCountryNameRu } from 'src/store/enqueue/selectors';
import { getRecommenderTypeForSearchPages } from 'src/utils/featureToggleSigma/analytics/abNewRecommender';
import type { SagaReturnType } from 'redux-saga/effects';
import { dataLayerPush, dataLayerPushParams } from '../../../helpers/gtm';
import { SEARCH_SUCCESS, SEARCH_TIMED_OUT } from '../constants/search';
import { TOGGLE_SUBSCRIBE_POPUP } from '../constants/view';
import { getPlainFilters } from '../selectors/filters';
import {
  hotelsListSelector,
  hotelsCountSelector,
} from '../store/hotels/selectors';
import { HOTEL_CLICKED } from '../constants/hotels';
import {
  getHotelsFlightSeats,
  getHotelsAccommodationAvailability,
  getHotelsIds,
  getHotelsSurchargesByLevel,
  getHotelsTourIds,
  bundleIdsSelector,
  acmComponentIdsSelector,
} from '../selectors/analytics';
import { getParams, getRequestId } from '../selectors/searchParams';
import {
  getOperatorsStatus,
  isSearchCompleteSelector,
} from '../searchStatus/selectors';
import { getSortField, getSortOrder } from '../selectors/sorting';
import { getIsSubscribeShown } from '../selectors/view';
import { takeOnce } from '../helpers/effects';
import { setEmptyRegion } from '../store/emptySearch/slices';
import { setShowPenalizedHotels } from '../store/emptyFiltration/slices';
import { isEmptyFiltrationSelector } from '../store/emptyFiltration/selectors';
import { ANALYTICS_CLICK_BELL_SUBSCRIBE } from '../constants/analytics';
import { setHotels } from '../store/hotels/slice';
import { operatorsStatusChanged } from '../searchStatus/slice';
import { parseTourSearchParams } from '../helpers/hitStoreParsers';
import { entryRulesClickedAction } from '../store/travelRequirements/actions';

/**
 * Отправляет данные в Travel Analytics.
 * @param action
 * @param isForce
 * @returns {Object}
 */
function* trackSearchCompleteEvent(action, isForce = false) {
  try {
    if (isForce || (yield select(isSearchCompleteSelector))) {
      const {
        payload: { originalSortBy },
      } = action;
      const hotelsList = yield select(hotelsListSelector);
      const hotelIds = yield select(getHotelsIds);
      const hotelTourIds = yield select(getHotelsTourIds);
      const hotelSurchargesByLevel = yield select(getHotelsSurchargesByLevel);
      const accommodationAvailability = yield select(
        getHotelsAccommodationAvailability,
      );
      const flightSeats = yield select(getHotelsFlightSeats);
      const sortField = yield select(getSortField);
      let searchParams = yield select(getParams);
      const localization = getLocalizationValues();
      const bundleIds = yield select(bundleIdsSelector);
      const acmComponentIds = yield select(acmComponentIdsSelector);

      searchParams = {
        ...searchParams,
        hotel_ids: hotelIds,
        tour_ids: hotelTourIds,
        surcharges_by_level: hotelSurchargesByLevel,
        accommodation_availability: accommodationAvailability,
        flight_seats: flightSeats,
        bundle_ids: bundleIds,
        acm_component_ids: acmComponentIds,
      };

      const isMobile = yield select(getIsMobile);
      const searchType = yield select(searchTypeSelector);

      const params = parseTourSearchParams({
        filterParams: yield select(getPlainFilters),
        requestId: yield select(getRequestId),
        result: hotelsList,
        hotelsCount: yield select(hotelsCountSelector),
        status: yield select(getOperatorsStatus),
        sortBy: sortField,
        sortOrder: yield select(getSortOrder),
        searchParams,
        searchType,
        originalSortBy,
        localization,
      });

      const { group } = yield call(
        getRecommenderTypeForSearchPages,
        isMobile,
        searchType,
      );

      dataLayerPush({
        event: isForce ? 'ccx-pageview' : 'ccx-event',
        search_params: params,
        custom_params: {
          map_type: 'yandex',
          recommenderGroup: group,
        },
      });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

function* openSubscription() {
  const isSubscribeShown = yield select(getIsSubscribeShown);

  if (isSubscribeShown) {
    dataLayerPush({
      event: 'pricewatch_subscription_form_expanded',
    });
  }
}

function emptySearchRegion() {
  dataLayerPush({
    event: 'empty_search',
  });
}

/** Пустая выдача по фильтрам */

// под фильтры не попал ни один отель
function* showEmptySearchWithFilters() {
  const isEmptySearchWithFilters = yield select(isEmptyFiltrationSelector);

  if (!isEmptySearchWithFilters) return;

  dataLayerPush({
    event: 'empty_tour_search_with_filters',
  });
}

// показаны отели, в которых нет части активных фильтров
function showPenalizedHotels() {
  dataLayerPush({
    event: 'show_penalized_hotels',
  });
}

// клик на отель, у которого нет части фильтров
function clickPenalizedHotel() {
  dataLayerPush({
    event: 'click_penalized_hotel',
  });
}

function clickBellSubscribe() {
  dataLayerPushParams({ ab_subscription: 'displayed' });
}

// переход по ссылке на лендинг с правилами въезда в страну
function* clickCountryEntryRules() {
  const countryEntryRules: SagaReturnType<
    typeof getSearchParamsToCountryNameRu
  > = yield select(getSearchParamsToCountryNameRu);

  if (!countryEntryRules) return;

  dataLayerPush({
    event: 'country_entry_rules',
    custom_params: { countryEntryRules },
  });
}

export default function* analyticsSaga() {
  yield all([
    takeOnce(SEARCH_SUCCESS, partialRight(trackSearchCompleteEvent, true)),
    takeOnce(ANALYTICS_CLICK_BELL_SUBSCRIBE, clickBellSubscribe),
    takeEvery(setEmptyRegion.type, emptySearchRegion),
    takeLatest(
      [operatorsStatusChanged.type, setHotels.type, SEARCH_TIMED_OUT],
      trackSearchCompleteEvent,
    ),
    takeEvery(TOGGLE_SUBSCRIBE_POPUP, openSubscription),
    takeEvery(SEARCH_SUCCESS, showEmptySearchWithFilters),
    takeOnce(setShowPenalizedHotels.type, showPenalizedHotels),
    takeEvery(HOTEL_CLICKED, clickPenalizedHotel),
    takeEvery(entryRulesClickedAction, clickCountryEntryRules),
  ]);
}
